import {Component, Input, Output, EventEmitter, ViewChild, ElementRef} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import imageCompression from 'browser-image-compression';
import * as Sentry from '@sentry/angular';
import {blobToArrayBuffer} from '../../../../../../shared/utils';
import {FileDialogData, FilePreviewResponse} from '../../../../../../models/file.model';
import {FilePreviewDialogComponent} from '../../../../../file-preview/file-preview-dialog.component';
import {SimpleDialogComponent} from '../../../../../../components/simple-dialog/simple-dialog.component';
import {Answer, Answered} from '../../../../shared/models/chat.model';

@UntilDestroy()
@Component({
  selector: 'app-button-file',
  templateUrl: './file.component.html',
  styleUrls: ['./file.component.scss']
})

export class FileComponent {
  private fileExtensionRegex = /\.\w+$/;

  @Input() public answer: Answer;
  @Input() public reference: string;
  @Output() private answered: EventEmitter<Answered> = new EventEmitter<Answered>();

  @ViewChild('nativeCamera') nativeCamera: ElementRef;
  @ViewChild('filePicker') filePicker: ElementRef;

  constructor(
    private dialog: MatDialog,
    private snackbar: MatSnackBar,
    private router: Router
  ) {}

  public async fileTaken(files: FileList): Promise<void> {
    if (!files.length) {
      return;
    }

    const file = files.item(0);

    const data: FileDialogData = this.buildDataButtons();
    const pattern = /(\.jpg|\.jpeg|\.png|\.webp|\.pdf|\.tiff|\.jfiff|\.gif)$/;
    // const hasExtension = this.fileExtensionRegex.test(file?.name);

    if (!pattern.exec(file?.name?.toLowerCase())) {
      SimpleDialogComponent
        .open(this.dialog, {
          title: 'Arquivo inválido',
          content: 'São aceitos arquivos apenas nos formatos: JPG, PNG, PDF, GIF, TIFF, WEBP, JFIF',
          okButton: 'OK',
          disableClose: true
        })
        .beforeClosed()
        .pipe(untilDestroyed(this))
        .subscribe();

      return;
    }

    try {
      data.file = await this.setFile(file);
    } catch (e) {
      this.snackbar.open(
        'Erro ao carregar arquivo. Confira se é um formato aceito (png, jpg, pdf) e tente novamente.',
        null,
        {duration: 2000}
      );
    }

    FilePreviewDialogComponent
      .open(this.dialog, data, this.router)
      .beforeClosed()
      .pipe(untilDestroyed(this))
      .subscribe(
        (preview: FilePreviewResponse) => this.setAnswer(preview),
        (e: Error) => Sentry.captureException(e)
      );
  }

  public pick(): void {
    if (this.answer.type === 'picture') {
      this.nativeCamera.nativeElement.value = '';
      this.nativeCamera.nativeElement.click();
    } else {
      this.filePicker.nativeElement.value = '';
      this.filePicker.nativeElement.click();
    }
  }

  private async setFile(file: File): Promise<File> {
    const buffer = await blobToArrayBuffer(file);
    const options = {
      // maxSizeMB: 3,
      maxWidthOrHeight: 4000,
      initialQuality: 0.8,
      useWebWorker: true
    };

    let value: File = new File([buffer], file.name, {type: file.type, lastModified: file.lastModified});

    if (file?.type?.includes('image/')) {
      const blob = await imageCompression(value, options);
      value = this.blobToFile(blob, value.name, value.lastModified);
    }

    return value;
  }

  private buildDataButtons(): FileDialogData {
    const data: FileDialogData = {};

    if (this.answer.type === 'print') {
      data.okButton = 'Foto ficou boa';
      data.noButton = 'Refazer foto';
    } else {
      data.okButton = 'Continuar';
      data.noButton = 'Escolher outro';
    }

    return data;
  }

  private blobToFile(theBlob: Blob, fileName: string, lastModified?: number): File {
    const blob: any = theBlob;
    // A Blob() is almost a File() - it's just missing the two properties below
    blob.lastModified = lastModified || (new Date().getTime() / 1000);
    blob.name = fileName;

    // Cast to a File() type
    return theBlob as File;
  }

  private setAnswer(preview: FilePreviewResponse): void {
    if (preview) {
      this.answered.emit(this.buildAnswer(preview?.label, preview?.value));
    }
  }

  private buildAnswer(label: string, value): Answered {
    return {
      label,
      file: true,
      send: {value}
    };
  }
}
